/**
 * Deterministic mock OHLCV candles for UI preview (no live market data).
 */

export const TIMEFRAMES = [
    { id: '10m', labelKey: 'analysis_tf_10m', minutes: 10, count: 180, volatility: 0.004 },
    { id: '1h', labelKey: 'analysis_tf_1h', minutes: 60, count: 180, volatility: 0.01 },
    { id: '1d', labelKey: 'analysis_tf_1d', minutes: 60 * 24, count: 160, volatility: 0.025 },
    { id: '1w', labelKey: 'analysis_tf_1w', minutes: 60 * 24 * 7, count: 160, volatility: 0.045 },
];

export function getTimeframe(id) {
    return TIMEFRAMES.find((tf) => tf.id === id) || TIMEFRAMES[2];
}

function hashSeed(input) {
    const str = String(input || 'MOCK');
    let h = 2166136261;
    for (let i = 0; i < str.length; i += 1) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function mulberry32(seed) {
    let t = seed >>> 0;
    return () => {
        t += 0x6D2B79F5;
        let r = Math.imul(t ^ (t >>> 15), 1 | t);
        r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
        return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
}

function pad2(n) {
    return String(n).padStart(2, '0');
}

export function formatCandleLabel(date, timeframeId) {
    const m = date.getUTCMonth() + 1;
    const d = date.getUTCDate();
    const hh = pad2(date.getUTCHours());
    const mm = pad2(date.getUTCMinutes());

    if (timeframeId === '1w') {
        return `${m}/${d}/${String(date.getUTCFullYear()).slice(2)}`;
    }
    if (timeframeId === '1d') {
        return `${m}/${d}`;
    }
    if (timeframeId === '1h') {
        return `${m}/${d} ${hh}:00`;
    }
    return `${hh}:${mm}`;
}

/**
 * @param {object} opts
 * @param {string} opts.symbol
 * @param {number} [opts.basePrice]
 * @param {string} [opts.timeframe] 10m | 1h | 1d
 * @param {number} [opts.count]
 */
export function buildMockCandles({
    symbol,
    basePrice,
    timeframe = '1d',
    count,
} = {}) {
    const tf = getTimeframe(timeframe);
    const candleCount = count || tf.count;
    const rand = mulberry32(hashSeed(`${symbol}:${tf.id}`));
    let price = Number(basePrice);
    if (!Number.isFinite(price) || price <= 0) {
        price = 40 + (hashSeed(symbol) % 180);
    }

    const candles = [];
    const stepMs = tf.minutes * 60 * 1000;
    // Align "now" to timeframe boundary
    const now = new Date();
    if (tf.id === '1d') {
        now.setUTCHours(0, 0, 0, 0);
    } else if (tf.id === '1h') {
        now.setUTCMinutes(0, 0, 0);
    } else {
        const mins = now.getUTCMinutes();
        now.setUTCMinutes(mins - (mins % 10), 0, 0);
    }

    for (let i = candleCount - 1; i >= 0; i -= 1) {
        const date = new Date(now.getTime() - i * stepMs);

        const drift = (rand() - 0.48) * price * tf.volatility;
        const open = price;
        const close = Math.max(0.5, open + drift);
        const wickUp = Math.abs(rand() * price * tf.volatility * 0.45);
        const wickDown = Math.abs(rand() * price * tf.volatility * 0.45);
        const high = Math.max(open, close) + wickUp;
        const low = Math.max(0.25, Math.min(open, close) - wickDown);
        const volume = Math.round(
            (tf.id === '1d' ? 200000 : tf.id === '1h' ? 40000 : 8000)
            + rand() * (tf.id === '1d' ? 1800000 : tf.id === '1h' ? 200000 : 40000),
        );

        candles.push({
            date: date.toISOString(),
            label: formatCandleLabel(date, tf.id),
            open: Number(open.toFixed(2)),
            high: Number(high.toFixed(2)),
            low: Number(low.toFixed(2)),
            close: Number(close.toFixed(2)),
            volume,
            timeframe: tf.id,
        });

        price = close;
    }

    return candles;
}

export function summarizeCandles(candles) {
    if (!candles?.length) {
        return {
            last: 0,
            change: 0,
            changePct: 0,
            high: 0,
            low: 0,
        };
    }
    const first = candles[0];
    const last = candles[candles.length - 1];
    const change = last.close - first.open;
    const changePct = first.open ? (change / first.open) * 100 : 0;
    const high = Math.max(...candles.map((c) => c.high));
    const low = Math.min(...candles.map((c) => c.low));
    return {
        last: last.close,
        change: Number(change.toFixed(2)),
        changePct: Number(changePct.toFixed(2)),
        high,
        low,
    };
}
